import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { Canvas, Object as FabricObject, Textbox, Group, Point, IText, Line } from 'fabric'
import { WorkSpaceThumbType, WorkSpaceDrawType } from "@/configs/canvas"
import { useFabricStore } from '@/store/modules/fabric'
import { useElementBounding } from '@vueuse/core'
import { FabricTool } from '@/app/fabricTool'
import { FabricGuide } from '@/app/fabricGuide'
import { HoverBorders } from '@/app/hoverBorders'
import { WheelScroll } from '@/app/wheelScroll'
import { FabricRuler } from '@/app/fabricRuler'
import { isMobile } from '@/utils/common'
import { FabricCanvas } from '@/app/fabricCanvas'
import { Keybinding } from '@/app/keybinding'
import { defaultControls, textboxControls } from '@/app/fabricControls'
import { getObjectsBoundingBox } from '@/extension/util/common'
import { useTemplatesStore, useMainStore } from '@/store'
import { RightStates } from '@/types/elements'
import useCommon from './useCommon'
import useHammer from './useHammer'
import useCenter from './useCenter'




const getCanvasInstance = () => {
  const fabricStore = useFabricStore()
  return fabricStore.canvas
}

export const disposeCanvas = async () => {
  const fabricStore = useFabricStore()
  const mainStore = useMainStore()
  if (fabricStore.canvas) {
    try {
      mainStore.setCanvasObject(undefined)
      await (fabricStore.canvas as any).dispose()
    } catch (e) {
      console.error('Error disposing canvas:', e)
    }
    fabricStore.canvas = null
  }
}

// 初始化配置
const initConf = () => {
  FabricObject.ownDefaults.objectCaching = false
  FabricObject.ownDefaults.borderColor = 'blue'
  FabricObject.ownDefaults.cornerColor = 'white'
  FabricObject.ownDefaults.cornerStrokeColor = '#c0c0c0'
  FabricObject.ownDefaults.borderOpacityWhenMoving = 1
  FabricObject.ownDefaults.borderScaleFactor = 1
  FabricObject.ownDefaults.cornerSize = 8
  FabricObject.ownDefaults.cornerStyle = 'rect'
  FabricObject.ownDefaults.centeredScaling = false
  FabricObject.ownDefaults.centeredRotation = true
  FabricObject.ownDefaults.transparentCorners = false
  // FabricObject.ownDefaults.rotatingPointOffset = 1
  // FabricObject.ownDefaults.lockUniScaling = true
  // FabricObject.ownDefaults.hasRotatingPoint = false
  FabricObject.ownDefaults.controls = defaultControls()

  Object.assign(Textbox.ownDefaults, { controls: textboxControls() })
  Object.assign(IText.ownDefaults, { controls: textboxControls() })

  const mixin = {
    getWidthHeight(noFixed = false): Point {
      const objScale = (this as FabricObject).getObjectScaling()
      const point = (this as FabricObject)._getTransformedDimensions({
        scaleX: objScale.x,
        scaleY: objScale.y,
      })
      if (!noFixed) {
        point.setX(point.x)
        point.setY(point.y)
      }
      return point
    },
    getHeight() {
      return this.getWidthHeight().y
    },
    getWidth() {
      return this.getWidthHeight().x
    },
  }

  Object.assign(FabricObject.prototype, mixin)
}

// 更新视图区长宽
const setCanvasTransform = () => {
  const canvas = getCanvasInstance()
  if (!canvas) return
  const fabricStore = useFabricStore()
  const { wrapperRef } = storeToRefs(fabricStore)
  if (!wrapperRef.value) return
  
  const width = wrapperRef.value.clientWidth
  const height = wrapperRef.value.clientHeight
  console.log('setCanvasTransform', { width, height })
  ;(canvas as any).setDimensions({ width, height })
  const objects = (canvas as any).getObjects().filter((ele: any) => !WorkSpaceThumbType.includes(ele.id))
  const boundingBox = getObjectsBoundingBox(objects)
  if (!boundingBox) return
  let boxWidth = boundingBox.width, boxHeight = boundingBox.height
  let centerX = boundingBox.centerX, centerY = boundingBox.centerY
  const workSpaceDraw = (canvas as any).getObjects().find((item: any) => item.id === WorkSpaceDrawType)
  if (workSpaceDraw) {
    boxWidth = workSpaceDraw.width
    boxHeight = workSpaceDraw.height
    centerX = (workSpaceDraw as any).left + (workSpaceDraw as any).width / 2
    centerY = (workSpaceDraw as any).top + (workSpaceDraw as any).height / 2 
  }
  
  // Smart zoom: Fit to container with padding
  const padding = 60
  const scaleX = (width - padding) / boxWidth
  const scaleY = (height - padding) / boxHeight
  const fitZoom = Math.min(scaleX, scaleY, 1.0)
  
  fabricStore.zoom = fitZoom
  
  ;(canvas as any).setZoom(fabricStore.zoom)
  ;(canvas as any).absolutePan(new Point(centerX, centerY).scalarMultiply(fabricStore.zoom).subtract((canvas as any).getCenterPoint()))
}

const initCanvas = () => {
  const fabricStore = useFabricStore()
  const { canvasRef } = storeToRefs(fabricStore)
  const fabricWidth = fabricStore.getWidth()
  const fabricHeight = fabricStore.getHeight()
  if (!canvasRef.value) return
  const canvas = new FabricCanvas(canvasRef.value, {
    width: fabricWidth,
    height: fabricHeight
  })
  fabricStore.canvas = canvas
  new FabricTool(canvas)
  new FabricGuide(canvas)
  new HoverBorders(canvas)
  new WheelScroll(canvas)
  ;(canvas as any).preserveObjectStacking = true
  ;(canvas as any).renderAll()
}

const initEvent = () => {
  const canvas = getCanvasInstance()
  if (!canvas) return
  const templatesStore = useTemplatesStore()
  const mainStore = useMainStore()
  
  canvas.on('object:modified', () => templatesStore.modifedElement())
  
  canvas.on('selection:created', (e) => {
    if (e.selected && e.selected.length > 0) {
      mainStore.setRightState(RightStates.ELEMENT_STYLE)
    }
  })
  
  canvas.on('selection:updated', (e) => {
    if (e.selected && e.selected.length > 0) {
      mainStore.setRightState(RightStates.ELEMENT_STYLE)
    }
  })
  
  canvas.on('selection:cleared', () => {
    mainStore.setRightState(RightStates.ELEMENT_CANVAS)
  })

  canvas.on('text:editing:entered', () => {
    mainStore.disableHotkeys = true
  })

  canvas.on('text:editing:exited', () => {
    mainStore.disableHotkeys = false
  })
}

// 初始化模板
const initTemplate = async (templateId?: number) => {
  const canvas = getCanvasInstance()
  if (!canvas || !(canvas as any).getContext()) return
  const { initCommon } = useCommon()
  const { initHammer } = useHammer()
  const templatesStore = useTemplatesStore()
  const { currentTemplate } = storeToRefs(templatesStore)
  console.log('initTemplate: currentTemplate check', { 
    hasTemplate: !!currentTemplate.value, 
    templateIndex: templatesStore.templateIndex,
    templateIdParam: templateId
  })
  
  if (currentTemplate.value) {
    const json = JSON.parse(JSON.stringify(currentTemplate.value))
    if (json.objects) {
      json.objects.forEach((obj: any) => {
        if (obj.type && obj.type.toLowerCase() === 'image') {
          obj.type = 'Image' // Force Fabric 6 type name
          obj.crossOrigin = 'anonymous'
        }
      })
    }
    await (canvas as any).loadFromJSON(json)
    ;(canvas as any).renderAll()
    setCanvasTransform()
    initCommon()
    initEvent()
    if (isMobile()) {
      initHammer()
    }
  }
}

export const initEditor = async (templateId?: number) => {
  const fabricStore = useFabricStore()
  const { wrapperRef } = storeToRefs(fabricStore)
  console.log('initEditor start', { templateId, hasWrapper: !!wrapperRef.value })
  
  // Dispose old canvas if still exists
  await disposeCanvas()
  
  initConf()
  initCanvas()
  await initTemplate(templateId)
}

export default (): [FabricCanvas] => [getCanvasInstance() as any]